import { type Observable } from 'rxjs'
import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
  type XhrSendRequestOptions,
} from '@/infra/api-client'
import type { Ordering } from '@/infra/paging'
import {
  type DocumentDownloadUrlResource,
  type DocumentResource,
  type DocumentPopulate,
  type ListDocumentsRequest,
  type CreateDocumentRequest,
  type UpdateDocumentRequest,
  type ZipFilesRequest,
  type AssignTypeRequest,
  type SearchDocumentsRequest,
  type SearchDocumentsFields,
} from '@/features/documents/api/resources/document'
import {
  type DocumentTypeResource,
  type GetManyDocumentTypesRequest,
} from '@/features/documents/api/resources/document-type'
import { type CreateDocumentAccessRequest } from '@/features/documents/api/resources/document-access'
import {
  type CreateDocumentVersionRequest,
  type DocumentVersionResource,
} from '@/features/documents/api/resources/document-version'
import { type ConfirmDocumentFileRequest } from '@/features/documents/api/resources/client-data-verification'
import { type DocumentsRealtimeMessage } from '@/features/documents/api/resources/realtime-messages'
import { toURLSearchParams } from '@/utils/url-util'

/**
 * Collection of APIs for documents.
 */
export class DocumentsApi {
  readonly documents
  readonly accesses
  readonly versions
  readonly types
  readonly clientDataVerification

  constructor(options: ApiClientOptions) {
    this.documents = new DocumentApi(options)
    this.accesses = new DocumentAccessApi(options)
    this.versions = new DocumentVersionApi(options)
    this.types = new DocumentTypeApi(options)
    this.clientDataVerification = new ClientDataVerificationApi(options)
  }
}

export class DocumentApi extends ApiClient {
  events(): Observable<DocumentsRealtimeMessage> {
    return this._events()
  }

  list(
    { order_by: orderBy, ...request }: ListDocumentsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<DocumentResource>> {
    const params = toURLSearchParams({ ...request })
    appendOrderBy(params, orderBy)
    return this._get('v3/documents', params, { signal })
  }

  search(
    { order_by: orderBy, ...request }: SearchDocumentsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<DocumentResource>> {
    const params = toURLSearchParams(
      { ...request },
      { arrayStyle: 'multi-key-bracket' },
    )
    appendOrderBy(params, orderBy)
    return this._get('v3/documents/search', params, { signal })
  }

  get(id: string, params?: DocumentPopulate): Promise<DocumentResource | null> {
    return this._get(`v3/documents/${id}`, toURLSearchParams({ ...params }))
  }

  create(request: CreateDocumentRequest): Promise<DocumentResource> {
    return this._post('v3/documents', request)
  }

  update(
    id: string,
    request: UpdateDocumentRequest,
  ): Promise<DocumentResource> {
    return this._patch(
      `v3/documents/${id}`,
      request,
      toURLSearchParams({ accesses: true, history: true }),
    )
  }

  archive(id: string): Promise<DocumentResource> {
    return this._patch(`v3/documents/${id}/archive`, undefined)
  }

  zipFiles(request: ZipFilesRequest): Promise<DocumentResource> {
    return this._post(`v3/documents/zipFiles`, request)
  }

  getDownloadUrl(
    id: string,
    preview = false,
  ): Promise<DocumentDownloadUrlResource> {
    const params = new URLSearchParams()
    if (preview !== undefined) {
      params.append('preview', preview.toString())
    }

    return this._get(`v3/documents/${id}/download`, params)
  }

  assignType(id: string, request: AssignTypeRequest) {
    return this._post<AssignTypeRequest, unknown>(
      `v3/documents/${id}/assign-type`,
      request,
    )
  }
}

export class DocumentTypeApi extends ApiClient {
  list(signal?: AbortSignal): Promise<DocumentTypeResource[]> {
    return this._get('v3/types', undefined, { signal })
  }

  getMany(
    request: GetManyDocumentTypesRequest,
  ): Promise<(DocumentTypeResource | null)[]> {
    return this._post(`v3/types/id-query`, request)
  }
}

export class DocumentAccessApi extends ApiClient {
  create(request: CreateDocumentAccessRequest): Promise<unknown> {
    return this._post('v3/accesses', request)
  }

  delete(id: string): Promise<unknown> {
    return this._delete(`v3/accesses/${id}`)
  }
}

export class DocumentVersionApi extends ApiClient {
  create(
    documentLongId: string,
    data: CreateDocumentVersionRequest,
    opts: CreateDocumentVersionOptions,
  ): Promise<DocumentVersionResource> {
    if (data.action.type !== 'ReplaceContents') {
      return this._post(
        `v3/documents/${documentLongId}/versions`,
        data,
        undefined,
        { signal: opts.signal },
      )
    }

    const form = new FormData()
    const { files, ...action } = data.action
    form.append('meta', JSON.stringify({ action, message: data.message }))
    files.forEach((f, i) =>
      form.append(`file${i + 1}`, f, sanitizeFileName(f.name)),
    )

    const request = new Request(
      this._createURL(`v3/documents/${documentLongId}/versions`),
      { method: 'POST', signal: opts.signal },
    )

    const xhrOpts: XhrSendRequestOptions = {
      form,
      onProgress: (e) => opts.onProgress(e),
    }

    return this._xhr(request, xhrOpts)
      .then(ApiClient.throwIfNotSuccessful)
      .then((r) => r.json() as Promise<DocumentVersionResource>)
  }
}

export class ClientDataVerificationApi extends ApiClient {
  confirmFile(
    documentLongId: string,
    versionId: string,
    fileId: string,
    request: ConfirmDocumentFileRequest,
  ): Promise<unknown> {
    return this._post(
      `v3/documents/${documentLongId}/versions/${versionId}/files/${fileId}/confirm`,
      request,
    )
  }
}

/**
 * Appends the orderings the way the legacy API parses nested query
 * params (`order_by[0][field]=title`).
 */
function appendOrderBy(
  params: URLSearchParams,
  orderBy: Ordering<SearchDocumentsFields>[] | undefined,
) {
  orderBy?.forEach((ordering, i) => {
    params.append(`order_by[${i}][field]`, ordering.field)
    params.append(`order_by[${i}][order]`, ordering.order)
  })
}

/**
 * Sanitizes a file name.
 * @param {string} str
 */
function sanitizeFileName(str: string) {
  return str.replace(/\//g, '_').replace(/\\/g, '_')
}

/**
 * Options for creating a document version.
 */
export interface CreateDocumentVersionOptions {
  signal?: AbortSignal
  onProgress(e: UploadProgress): void
}

export interface UploadProgress {
  total: number
  loaded: number
}
